import { Box, CircularProgress, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CoinWithSymbol, Transaction } from '../../../../../services/transaction.types';
import { UserDetails } from '../../../../../services/user.types';
import { getDisplayCoin } from '../../../../../utils/displayFormatters';

interface FeeSectionProps {
    formValues: Partial<Transaction>;
    currentCtUser: UserDetails | null;
    onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCoinChange: (value: CoinWithSymbol | null | string) => void;
    formatInput: (field: string, value: number | undefined) => string;
    hasMissingPrices?: boolean;
    isUpdatingPrices?: boolean;
    showBuyFields?: boolean;
    showSellFields?: boolean;
}

const FeeSection: React.FC<FeeSectionProps> = ({
    formatInput,
    formValues,
    currentCtUser,
    onAmountChange,
    onPriceChange,
    onValueChange,
    onCoinChange,
    hasMissingPrices,
    isUpdatingPrices,
    showBuyFields,
    showSellFields
}) => {
    const { t } = useTranslation();
    const allowedOptions = useCallback(() => {
        let options: Array<{ coin: string; ticker: string }> = [];
        const { buyCoin, sellCoin, buyTicker, sellTicker, feeCoin, feeTicker } = formValues;

        // If there's already a fee coin set, include it in the options
        if (feeCoin && feeCoin !== '') {
            options.push({ coin: feeCoin, ticker: feeTicker || feeCoin });
        }

        if (buyCoin && sellCoin && buyCoin === sellCoin) {
            options.push({ coin: buyCoin, ticker: buyTicker || buyCoin });
            return options;
        }

        if (showBuyFields && buyCoin && buyCoin !== '') {
            // Only add if not already in options
            if (!options.some(opt => opt.coin === buyCoin)) {
                options.push({ coin: buyCoin, ticker: buyTicker || buyCoin });
            }
        }

        if (showSellFields && sellCoin && sellCoin !== '') {
            // Only add if not already in options
            if (!options.some(opt => opt.coin === sellCoin)) {
                options.push({ coin: sellCoin, ticker: sellTicker || sellCoin });
            }
        }

        return options;
    }, [formValues.buyCoin, formValues.sellCoin, formValues.buyTicker, formValues.sellTicker, 
        formValues.feeCoin, formValues.feeTicker, showBuyFields, showSellFields]);

    const currentOptions = allowedOptions();
    // Always allow editing the fee coin when a fee amount is present
    const editAllowed = formValues.fee != null && formValues.fee > 0;
    
    // For outgoing-only transactions (like Spend), the fee coin should match the sell coin
    const isOutgoingOnly = !showBuyFields && showSellFields;
    // For incoming-only transactions, the fee coin should match the buy coin
    const isIncomingOnly = showBuyFields && !showSellFields;
    // For transfer transactions, handle specially
    const isTransfer = showBuyFields && formValues.accountIncoming;
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                {t('transactions.modal.form.fee')}
            </Typography>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: '1fr 1fr 1fr 1fr'
                    },
                    gap: 2,
                    mb: 2
                }}>
                    <TextField
                        fullWidth
                        label={t('transactions.modal.form.feeAmount')}
                        type="text"
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                        }}
                        value={formatInput('fee', formValues.fee)}
                        onChange={onAmountChange}
                        sx={{
                            '& .MuiInputBase-root': { borderRadius: 1 },
                        }}
                        InputProps={{
                            inputProps: { min: 0, step: '0.00000001' },
                        }}
                    />

                    {/* For outgoing-only transactions, always show the sell coin as the fee coin */}
                    {isOutgoingOnly && formValues.sellCoin ? (
                        <TextField
                            fullWidth
                            label={t('transactions.modal.form.feeCoin')}
                            value={getDisplayCoin(formValues.sellTicker, formValues.sellCoin)}
                            disabled={true}
                            sx={{
                                '& .MuiInputBase-root': { borderRadius: 1 },
                            }}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    ) : isIncomingOnly && formValues.buyCoin ? (
                        <TextField
                            fullWidth
                            label={t('transactions.modal.form.feeCoin')}
                            value={getDisplayCoin(formValues.buyTicker, formValues.buyCoin)}
                            disabled={true}
                            sx={{
                                '& .MuiInputBase-root': { borderRadius: 1 },
                            }}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    ) : isTransfer && formValues.buyCoin ? (
                        <TextField
                            fullWidth
                            label={t('transactions.modal.form.feeCoin')}
                            value={getDisplayCoin(formValues.buyTicker, formValues.buyCoin)}
                            disabled={true}
                            sx={{
                                '& .MuiInputBase-root': { borderRadius: 1 },
                            }}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    ) : currentOptions.length > 0 ? (
                        <Select
                            value={formValues.feeCoin || ''}
                            onChange={(e) => onCoinChange(e.target.value)}
                            label={t('transactions.modal.form.feeCoin')}
                            disabled={!editAllowed}
                            renderValue={() => getDisplayCoin(formValues.feeTicker, formValues.feeCoin)}
                            sx={{
                                '& .MuiInputBase-root': { borderRadius: 1 },
                            }}
                        >
                            {currentOptions.map(({ coin, ticker }, index) => (
                                <MenuItem key={index} value={coin}>
                                    {getDisplayCoin(ticker, coin)}
                                </MenuItem>
                            ))}
                        </Select>
                    ) : (
                        <TextField
                            fullWidth
                            label={t('transactions.modal.form.feeCoin')}
                            value={formValues.feeCoin || ''}
                            onChange={(e) => onCoinChange(e.target.value)}
                            disabled={!editAllowed}
                            sx={{
                                '& .MuiInputBase-root': { borderRadius: 1 },
                            }}
                        />
                    )}

                    <TextField
                        fullWidth
                        label={t('transactions.modal.form.pricePerUnit')}
                        type="text"
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                        }}
                        value={formatInput('feePricePerUnit', formValues.feePricePerUnit)}
                        onChange={onPriceChange}
                        error={hasMissingPrices && !formValues.feePricePerUnit}
                        helperText={hasMissingPrices && !formValues.feePricePerUnit ? 'Price required' : undefined}
                        sx={{
                            '& .MuiInputBase-root': { borderRadius: 1 },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {isUpdatingPrices ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        currentCtUser?.currency || ''
                                    )}
                                </InputAdornment>
                            ),
                            inputProps: { min: 0, step: '0.00000001' },
                        }}
                    />
                    <TextField
                        fullWidth
                        label={t('transactions.modal.form.feeValueInFiat')}
                        type="text"
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                        }}
                        value={formatInput('feeValueInFiat', formValues.feeValueInFiat)}
                        onChange={onValueChange}
                        sx={{
                            '& .MuiInputBase-root': { borderRadius: 1 },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {currentCtUser?.currency || ''}
                                </InputAdornment>
                            ),
                            inputProps: { min: 0, step: '0.00000001' },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default FeeSection;
